import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MatrixHeading from "./MatrixHeading";
import "./Domains.css";

/* ─────────────────────────────────────────────────────────────────
   Five domain "tiles" — collapsed strips on the left, the active
   one expands to a panel. The right column mirrors About Us
   (eyebrow → big heading → tagline → animated line → description),
   and its description swaps to whichever domain is active.
───────────────────────────────────────────────────────────────── */
const domains = [
  {
    id: "webdev",
    title: "Development",
    icon: "fa-laptop-code",
    content:
      "IEEE TEMS specializes in Web and App Development, dedicated to fostering creativity and technical skill. We host coding competitions, hands-on workshops for web and mobile interfaces, and intensive coding bootcamps that equip members to build responsive websites and dynamic applications.",
  },
  {
    id: "ml",
    title: "Machine Learning",
    icon: "fa-robot",
    content:
      "In Machine Learning, IEEE TEMS is the go-to hub for enthusiasts exploring cutting-edge algorithms, advanced data analysis, and practical AI applications. Our community actively engages in workshops, hackathons, and seminars to stay updated with the latest advancements in the field.",
  },
  {
    id: "cp",
    title: "Competitive Programming",
    icon: "fa-globe",
    content:
      "We empower members to master problem-solving and algorithmic challenges through contests, workshops, and personalized mentorship. Our flagship events, CodeRush and CodeClash, set the benchmark — CodeClash has gained national recognition, drawing over 1,000 participants from across the country.",
  },
  {
    id: "design",
    title: "Design",
    icon: "fa-pen-ruler",
    content:
      "In design, IEEE TEMS champions the harmonious fusion of technology and aesthetics. At the heart of our initiatives are UI/UX challenges that push members to craft engaging, user-friendly interfaces, alongside graphic design workshops that sharpen their artistic and creative skills.",
  },
  {
    id: "editorial",
    title: "Blogging & Vlogging",
    icon: "fa-newspaper",
    content:
      "IEEE TEMS extends its reach into effective communication through a dedicated platform for blogging and vlogging. We offer comprehensive training in content creation, video editing, and effective social media marketing — bridging the gap between experts and enthusiasts.",
  },
];

const Domains = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = domains[activeIndex];

  /* Trigger the MatrixHeading reveal when the section scrolls into
     view, just like About Us. Resets when the section leaves so the
     animation replays the next time the user scrolls back. */
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* On mobile, the active strip needs to take MUCH more of the row
     so longer titles ("Competitive Programming", "Blogging & Vlogging")
     fit on a single line. */
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(max-width: 900px)").matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 900px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const activeFlex = isMobile ? 14 : 6;
  const inactiveFlex = 1;

  return (
    <section id="Domains" ref={sectionRef}>
      <div className="dom-split">
        {/* HEADING block — appears at top on mobile, right-top on desktop */}
        <div className="dom-heading-block">

          <h2 className="dom-main-heading">
            <MatrixHeading text="Domains" active={isVisible} />
          </h2>

          <p className="dom-tagline">
            <span className="dom-tagline-row">FIVE PILLARS</span>
            <span className="dom-tagline-row">ONE COMMUNITY</span>
          </p>

          <div className="dom-heading-line" />
        </div>

        {/* STRIPS — appears middle on mobile, full-height left on desktop */}
        <div className="dom-strips">
          {domains.map((d, i) => {
            const isActive = activeIndex === i;
            return (
              <motion.div
                key={d.id}
                className={`dom-strip${isActive ? " is-active" : ""}`}
                initial={false}
                animate={{
                  flexGrow: isActive ? activeFlex : inactiveFlex,
                }}
                transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                onMouseEnter={() => setActiveIndex(i)}
                onFocus={() => setActiveIndex(i)}
                onClick={() => setActiveIndex(i)}
                tabIndex={0}
                role="button"
                aria-label={d.title}
              >
                <div className="dom-strip-bg" />
                <div className="dom-strip-overlay" />

                <div className="dom-strip-icon-wrap">
                  <span className={`fa-solid ${d.icon} dom-strip-icon`} />
                </div>

                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      key="hlabel"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                      className="dom-strip-hlabel"
                    >
                      <span className="dom-strip-index">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="dom-strip-title">{d.title}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* CONTENT — appears bottom on mobile, right-bottom on desktop */}
        <div className="dom-content-block">
          <div className="dom-content-wrap">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="dom-content"
              >
                <p className="dom-para">{active.content}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="dom-mission-text">
            Explore the disciplines that power every initiative at IEEE TEMS VIT.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Domains;
